/**
 * Token typeahead — V3 R2.
 *
 * "Typing tokens must feel like Linear/Slack, not a memory test."
 *
 * A headless hook plus a menu, driven by a plain `<input>`. It watches the text
 * before the caret for a trigger (`@`, `#`, `!`, `~`, `+`, or a date-ish
 * fragment), offers matches, and on selection splices the *canonical* token
 * text back in — so the server-side parser in `packages/types/quickAdd.ts` is
 * untouched. This is presentation only.
 *
 * Keyboard-only by construction: ↑↓ move, Enter/Tab select, Esc dismisses. The
 * mouse is optional everywhere.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PRIORITIES, TASK_TYPES } from '@virtuetasks/types';
import { UserAvatar } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export type TriggerKind = 'person' | 'project' | 'priority' | 'type' | 'date' | 'estimate';

export interface TypeaheadItem {
  id: string;
  /** What gets spliced into the text, without the trigger character. */
  token: string;
  label: string;
  hint?: string;
  kind: TriggerKind;
  avatarFor?: string;
}

const PRIORITY_MEANING: Record<string, string> = {
  P0: 'Drop everything',
  P1: 'This week',
  P2: 'Normal',
  P3: 'Whenever',
};

const PRIORITY_COLOUR: Record<string, string> = {
  P0: '#C2413A',
  P1: '#C9762B',
  P2: '#4A7CB5',
  P3: '#7C838A',
};

/** Weekday and month names the date resolver understands. */
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/**
 * Resolves a partial date phrase to a concrete date.
 *
 * Deliberately forgiving on prefixes — `tom`, `fri`, `25 a`, `in 3` — because
 * the point is to confirm what the parser will do *before* you finish typing.
 * Returns null when the fragment is not date-ish, which is most of the time.
 */
export function resolveDateFragment(fragment: string, now = new Date()): Date | null {
  const text = fragment.trim().toLowerCase();
  if (!text) return null;

  const startOfDay = (d: Date): Date => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  if ('today'.startsWith(text) && text.length >= 3) return startOfDay(now);
  if ('tomorrow'.startsWith(text) && text.length >= 3) {
    const d = startOfDay(now);
    d.setDate(d.getDate() + 1);
    return d;
  }

  // `in 3`, `in 3 d`, `in 2 weeks`
  const relative = /^in\s+(\d+)\s*(d|day|days|w|week|weeks)?$/.exec(text);
  if (relative) {
    const amount = Number(relative[1]);
    const weeks = (relative[2] ?? '').startsWith('w');
    const d = startOfDay(now);
    d.setDate(d.getDate() + amount * (weeks ? 7 : 1));
    return d;
  }

  // A weekday name or prefix — always the *next* one.
  const weekdayIndex = WEEKDAYS.findIndex((w) => w.startsWith(text) && text.length >= 2);
  if (weekdayIndex >= 0) {
    const d = startOfDay(now);
    const delta = (weekdayIndex - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + delta);
    return d;
  }

  // `25 sep`, `25 s`, `3 jan`
  const dayMonth = /^(\d{1,2})\s+([a-z]+)$/.exec(text);
  if (dayMonth) {
    const monthIndex = MONTHS.findIndex((m) => m.startsWith(dayMonth[2]));
    if (monthIndex >= 0) {
      const day = Number(dayMonth[1]);
      const d = startOfDay(new Date(now.getFullYear(), monthIndex, day));
      // A date already gone means next year.
      if (d < startOfDay(now)) d.setFullYear(d.getFullYear() + 1);
      return d;
    }
  }

  return null;
}

export function formatDateChip(date: Date): string {
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

export interface TypeaheadSources {
  people: { _id: string; name: string; designation?: string; loadPercent?: number }[];
  projects: { _id: string; key: string; name: string; icon: string }[];
}

interface ActiveTrigger {
  kind: TriggerKind;
  /** Index of the trigger character in the value. */
  start: number;
  query: string;
}

/** Finds the trigger the caret currently sits inside, if any. */
function detectTrigger(value: string, caret: number): ActiveTrigger | null {
  const before = value.slice(0, caret);

  // A trigger token runs from its symbol to the caret with no whitespace.
  const symbolMatch = /(^|\s)([@#!~+])([^\s]*)$/.exec(before);
  if (symbolMatch) {
    const symbol = symbolMatch[2];
    const query = symbolMatch[3];
    const start = before.length - query.length - 1;
    const kind: TriggerKind =
      symbol === '@' ? 'person'
      : symbol === '#' ? 'project'
      : symbol === '!' ? 'priority'
      : symbol === '~' ? 'type'
      : 'estimate';
    return { kind, start, query };
  }

  // Dates have no symbol: look at the trailing words for something date-ish.
  const words = before.split(/\s+/);
  for (let take = Math.min(3, words.length); take >= 1; take -= 1) {
    const fragment = words.slice(words.length - take).join(' ');
    if (!fragment.trim()) continue;
    if (resolveDateFragment(fragment)) {
      return { kind: 'date', start: before.length - fragment.length, query: fragment };
    }
  }

  return null;
}

function buildItems(trigger: ActiveTrigger, sources: TypeaheadSources): TypeaheadItem[] {
  const q = trigger.query.toLowerCase();

  if (trigger.kind === 'person') {
    return sources.people
      .filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          (p.designation ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8)
      .map((p) => ({
        id: p._id,
        // The canonical token is the first name, lower-cased — what the
        // server-side parser resolves against.
        token: p.name.split(/\s+/)[0].toLowerCase(),
        label: p.name,
        hint: [p.designation, p.loadPercent != null ? `${p.loadPercent}% this week` : null]
          .filter(Boolean)
          .join(' · '),
        kind: 'person',
        avatarFor: p._id,
      }));
  }

  if (trigger.kind === 'project') {
    return sources.projects
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q))
      .slice(0, 8)
      .map((p) => ({
        id: p._id,
        token: p.key,
        label: `${p.icon} ${p.name}`,
        hint: p.key,
        kind: 'project',
      }));
  }

  if (trigger.kind === 'priority') {
    return PRIORITIES.filter((p) => !q || p.toLowerCase().startsWith(q)).map((p) => ({
      id: p,
      token: p,
      label: p,
      hint: PRIORITY_MEANING[p],
      kind: 'priority',
    }));
  }

  if (trigger.kind === 'type') {
    return TASK_TYPES.filter((t) => !q || t.startsWith(q))
      .slice(0, 8)
      .map((t) => ({ id: t, token: t, label: t, kind: 'type' }));
  }

  if (trigger.kind === 'estimate') {
    if (!/^\d/.test(q)) return [];
    const normalised = /^(\d+(?:\.\d+)?)(h|m)?$/.exec(q);
    if (!normalised) return [];
    const token = `${normalised[1]}${normalised[2] ?? 'h'}`;
    return [{ id: token, token, label: token, hint: `${token} estimate`, kind: 'estimate' }];
  }

  const date = resolveDateFragment(trigger.query);
  if (!date) return [];
  return [
    {
      id: 'date',
      token: trigger.query.trim(),
      label: trigger.query.trim(),
      hint: `→ ${formatDateChip(date)}`,
      kind: 'date',
    },
  ];
}

export interface UseTypeaheadResult {
  items: TypeaheadItem[];
  activeIndex: number;
  open: boolean;
  trigger: ActiveTrigger | null;
  /** Attach to the input's onKeyDown; returns true when it handled the key. */
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => boolean;
  /** Attach to the input's onChange — captures the caret with the value. */
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Attach to onKeyUp/onClick so arrow keys re-detect the trigger. */
  onCaretMove: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  select: (item: TypeaheadItem) => void;
  dismiss: () => void;
}

export function useTokenTypeahead({
  value,
  onChange,
  inputRef,
  sources,
}: {
  value: string;
  onChange: (next: string, caret: number) => void;
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  sources: TypeaheadSources;
}): UseTypeaheadResult {
  // Caret defaults to the end of the text. The input is inside a dialog that
  // mounts lazily, so attaching DOM listeners in an effect misses it entirely —
  // the caret stayed 0 and no trigger was ever detected. It is captured from
  // the React events instead, which cannot be missed.
  const [caret, setCaret] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const effectiveCaret = Math.min(caret ?? value.length, value.length);

  const trigger = useMemo(
    () => (dismissed ? null : detectTrigger(value, effectiveCaret)),
    [value, effectiveCaret, dismissed],
  );
  const items = useMemo(() => (trigger ? buildItems(trigger, sources) : []), [trigger, sources]);

  useEffect(() => setActiveIndex(0), [trigger?.kind, trigger?.query]);
  useEffect(() => setDismissed(false), [value]);

  const select = useCallback(
    (item: TypeaheadItem) => {
      if (!trigger) return;
      const symbol =
        item.kind === 'person' ? '@'
        : item.kind === 'project' ? '#'
        : item.kind === 'priority' ? '!'
        : item.kind === 'type' ? '~'
        : item.kind === 'estimate' ? '+'
        : '';
      const replacement = `${symbol}${item.token} `;
      const end = trigger.start + (symbol ? 1 : 0) + trigger.query.length;
      const next = value.slice(0, trigger.start) + replacement + value.slice(end);
      const nextCaret = trigger.start + replacement.length;

      onChange(next, nextCaret);
      setDismissed(true);
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(nextCaret, nextCaret);
        setCaret(nextCaret);
      });
    },
    [trigger, value, onChange, inputRef],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): boolean => {
      if (items.length === 0) return false;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        select(items[activeIndex]);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        // Radix's dismiss layer listens on the document in the *capture*
        // phase, so it sees Escape before this handler and neither
        // stopPropagation nor stopImmediatePropagation can reach it. The host
        // dialog must therefore consult `state.open` in `onEscapeKeyDown` —
        // see QuickAddDialog. This only clears the suggestions.
        setDismissed(true);
        return true;
      }
      return false;
    },
    [items, activeIndex, select],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCaret(e.target.selectionStart ?? e.target.value.length);
      onChange(e.target.value, e.target.selectionStart ?? e.target.value.length);
    },
    [onChange],
  );

  const onCaretMove = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCaret((e.target as HTMLInputElement).selectionStart ?? null);
    },
    [],
  );

  return {
    items,
    activeIndex,
    open: items.length > 0,
    trigger,
    onKeyDown,
    onInputChange,
    onCaretMove,
    select,
    dismiss: () => setDismissed(true),
  };
}

export function TypeaheadMenu({
  state,
  className,
}: {
  state: UseTypeaheadResult;
  className?: string;
}) {
  if (!state.open) return null;

  return (
    <ul
      role="listbox"
      aria-label="Suggestions"
      data-testid="typeahead-menu"
      className={cn(
        'vt-surface vt-elevated absolute z-50 max-h-64 w-full overflow-y-auto p-1',
        className,
      )}
    >
      {state.items.map((item, i) => (
        <li key={`${item.kind}-${item.id}`}>
          <button
            type="button"
            role="option"
            aria-selected={i === state.activeIndex}
            data-testid="typeahead-option"
            onMouseDown={(e) => {
              e.preventDefault();
              state.select(item);
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[13px]',
              i === state.activeIndex ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-2)]',
            )}
          >
            {item.kind === 'person' && item.avatarFor ? (
              <UserAvatar id={item.avatarFor} name={item.label} size={20} />
            ) : item.kind === 'priority' ? (
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: PRIORITY_COLOUR[item.token] }}
                aria-hidden
              />
            ) : null}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {item.hint && <span className="shrink-0 text-[11px] vt-muted">{item.hint}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}
